import { useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type {
  ImportMapping,
  ImportPreviewResponse,
  ImportStatusResponse,
} from '@outreach/contracts';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/components/ui/sonner';
import { api, ApiRequestError } from '@/lib/api';

type Field = 'email' | 'phone' | 'firstName' | 'lastName';
const FIELDS: Array<{ key: Field; label: string }> = [
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Phone' },
  { key: 'firstName', label: 'First name' },
  { key: 'lastName', label: 'Last name' },
];

function autoMap(headers: string[]): Record<Field, string> {
  const find = (...needles: string[]) =>
    headers.find((h) => needles.some((n) => h.toLowerCase().includes(n))) ?? '';
  return {
    email: find('email', 'e-mail'),
    phone: find('phone', 'mobile', 'tel'),
    firstName: find('first'),
    lastName: find('last'),
  };
}

async function toBase64(file: File): Promise<string> {
  const dataUrl: string = await new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(r.error);
    r.readAsDataURL(file);
  });
  return dataUrl.split(',')[1] ?? '';
}

function parseHeaderLine(text: string, delimiter: string): string[] {
  const stripped = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text; // drop BOM
  const firstLine = stripped.split(/\r?\n/)[0] ?? '';
  return firstLine.split(delimiter).map((h) => h.trim().replace(/^"|"$/g, ''));
}

export function ImportDialog({ trigger }: { trigger: ReactNode }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [base64, setBase64] = useState('');
  const [rawText, setRawText] = useState('');
  const [delimiter, setDelimiter] = useState(',');
  const [headers, setHeaders] = useState<string[]>([]);
  const [mapping, setMapping] = useState<Record<Field, string>>({
    email: '',
    phone: '',
    firstName: '',
    lastName: '',
  });
  const [preview, setPreview] = useState<ImportPreviewResponse | null>(null);
  const [result, setResult] = useState<ImportStatusResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function reset() {
    setFile(null);
    setBase64('');
    setHeaders([]);
    setMapping({ email: '', phone: '', firstName: '', lastName: '' });
    setPreview(null);
    setResult(null);
    setError(null);
  }

  async function onFile(f: File) {
    setError(null);
    setPreview(null);
    setResult(null);
    setFile(f);
    const [b64, text] = await Promise.all([toBase64(f), f.text()]);
    setBase64(b64);
    setRawText(text);
    const hdrs = parseHeaderLine(text, delimiter);
    setHeaders(hdrs);
    setMapping(autoMap(hdrs));
  }

  function onDelimiterChange(d: string) {
    setDelimiter(d);
    if (rawText) {
      const hdrs = parseHeaderLine(rawText, d);
      setHeaders(hdrs);
      setMapping(autoMap(hdrs));
    }
  }

  function buildMapping(): ImportMapping {
    const m: ImportMapping = {};
    for (const { key } of FIELDS) if (mapping[key]) m[key] = mapping[key];
    if (delimiter !== ',') m.delimiter = delimiter;
    return m;
  }

  async function onPreview() {
    setError(null);
    setBusy(true);
    try {
      const res = await api.imports.preview({
        filename: file?.name,
        contentBase64: base64,
        mapping: buildMapping(),
      });
      setPreview(res);
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'Preview failed');
    } finally {
      setBusy(false);
    }
  }

  async function onCommit() {
    if (!preview) return;
    setBusy(true);
    setError(null);
    try {
      const key = crypto.randomUUID();
      const res = await api.imports.commit(preview.importId, key);
      setResult(res);
      toast.success('Import complete');
      void qc.invalidateQueries({ queryKey: ['contacts'] });
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'Commit failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) reset();
      }}
    >
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Import contacts</DialogTitle>
          <DialogDescription>
            Upload a CSV, map columns, preview, then commit. Nothing changes until you import.
          </DialogDescription>
        </DialogHeader>

        {/* Step 1: file */}
        <div className="space-y-1.5">
          <Label htmlFor="csv">CSV file</Label>
          <input
            id="csv"
            type="file"
            accept=".csv,text/csv"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void onFile(f);
            }}
            className="block w-full text-sm text-foreground file:mr-3 file:rounded-md file:border file:border-input file:bg-background file:px-3 file:py-1.5 file:text-sm"
          />
        </div>

        {/* Step 2: mapping */}
        {headers.length > 0 && !result && (
          <div className="space-y-3">
            <div className="flex items-end justify-between gap-2">
              <p className="text-sm font-medium">Map columns</p>
              <div className="flex items-center gap-2">
                <Label htmlFor="delimiter" className="text-xs">
                  Delimiter
                </Label>
                <select
                  id="delimiter"
                  value={delimiter}
                  onChange={(e) => onDelimiterChange(e.target.value)}
                  className="h-8 rounded-md border border-input bg-background px-2 text-sm"
                >
                  <option value=",">Comma</option>
                  <option value=";">Semicolon</option>
                  <option value={'\t'}>Tab</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {FIELDS.map(({ key, label }) => (
                <div key={key} className="space-y-1.5">
                  <Label htmlFor={`map-${key}`}>{label}</Label>
                  <select
                    id={`map-${key}`}
                    value={mapping[key]}
                    onChange={(e) => setMapping((m) => ({ ...m, [key]: e.target.value }))}
                    className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <option value="">— none —</option>
                    {headers.map((h) => (
                      <option key={h} value={h}>
                        {h}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
            <Button size="sm" variant="outline" onClick={onPreview} disabled={busy || !base64}>
              {busy && !preview ? 'Analyzing…' : 'Preview'}
            </Button>
          </div>
        )}

        {/* Step 3: preview */}
        {preview && !result && (
          <div className="space-y-3">
            <p className="text-sm font-medium">Preview ({preview.counts.total} rows)</p>
            <div className="flex flex-wrap gap-2">
              <Badge variant="success">{preview.counts.valid} valid</Badge>
              <Badge variant="muted">{preview.counts.existing} existing</Badge>
              <Badge variant={preview.counts.invalid ? 'destructive' : 'muted'}>
                {preview.counts.invalid} invalid
              </Badge>
              <Badge variant={preview.counts.conflicting ? 'warning' : 'muted'}>
                {preview.counts.conflicting} conflicts
              </Badge>
              <Badge variant="muted">{preview.counts.consentMissing} consent-missing</Badge>
            </div>
            {preview.conflicts.length > 0 && (
              <div className="max-h-32 overflow-y-auto rounded-md border p-2 text-xs">
                {preview.conflicts.slice(0, 20).map((c) => (
                  <div key={c.rowIndex} className="flex justify-between gap-2 py-0.5">
                    <span>row {c.rowIndex + 1}</span>
                    <span className="text-muted-foreground">{c.reason}</span>
                  </div>
                ))}
              </div>
            )}
            {preview.errorsAvailable && (
              <a
                href={api.imports.errorsUrl(preview.importId)}
                className="text-sm text-primary underline-offset-4 hover:underline"
              >
                Download error rows (CSV)
              </a>
            )}
            <div>
              <Button onClick={onCommit} disabled={busy}>
                {busy ? 'Importing…' : `Import ${preview.counts.valid} rows`}
              </Button>
            </div>
          </div>
        )}

        {/* Step 4: result */}
        {result && (
          <div className="space-y-2">
            <p className="text-sm font-medium">Import complete</p>
            <div className="flex flex-wrap gap-2">
              <Badge variant="success">{result.counts.created ?? 0} created</Badge>
              <Badge variant="muted">{result.counts.updated ?? 0} updated</Badge>
              <Badge variant="muted">{result.counts.unchanged ?? 0} unchanged</Badge>
              <Badge variant={result.counts.conflict ? 'warning' : 'muted'}>
                {result.counts.conflict ?? 0} conflicts
              </Badge>
            </div>
            <Button size="sm" variant="outline" onClick={() => setOpen(false)}>
              Done
            </Button>
          </div>
        )}

        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}
